/**
 * Analytics API
 *
 * Handles all analytics-related API calls to the backend.
 * Includes retry logic with exponential backoff for rate limit handling.
 */

import apiClient from './apiClient';

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch analytics with retry logic for rate limiting
 *
 * @param {Object} requestBody - Request body
 * @param {number} retries - Number of retries remaining
 * @param {number} attempt - Current attempt number (for exponential backoff)
 * @returns {Promise<Object>} Analytics data
 */
const fetchAnalyticsWithRetry = async (requestBody, retries = 3, attempt = 1) => {
  try {
    const response = await apiClient.post('/api/analytics/summary', requestBody);

    // apiClient already extracts response.data, so just return it
    if (response && typeof response === 'object' && response.summary) {
      return response;
    }

    // If response is wrapped, try to extract
    if (response?.data) {
      return response.data;
    }

    return response;
  } catch (error) {
    // Handle rate limit errors (429)
    if (error.status === 429 && retries > 0) {
      const delay = 2000 * Math.pow(2, attempt - 1);
      await sleep(delay);
      return fetchAnalyticsWithRetry(requestBody, retries - 1, attempt + 1);
    }

    // Handle network errors or 404 - return mock data
    if (error.status === 404 || error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
      return getMockAnalytics();
    }

    const errorMessage = error.message || 'Failed to load analytics data';
    throw new Error(errorMessage);
  }
};

/**
 * Fetch analytics summary data
 *
 * @param {Object} params - Query parameters
 * @param {number} [params.start_date] - Start date timestamp in milliseconds
 * @param {number} [params.end_date] - End date timestamp in milliseconds
 * @param {string} [params.location_id] - Location ID to filter by
 * @returns {Promise<Object>} Analytics data
 */
export const getAnalyticsSummary = async (params = {}) => {
  const requestBody = {};

  if (params.start_date && params.end_date) {
    requestBody.start_date = params.start_date;
    requestBody.end_date = params.end_date;
  }

  if (params.location_id && params.location_id !== 'all') {
    requestBody.location_id = params.location_id;
  }

  if (params.group_id) {
    requestBody.group_id = params.group_id;
  }

  return fetchAnalyticsWithRetry(requestBody, 3, 1);
};

/**
 * Mock analytics data for development
 * This will be used until the backend endpoint is ready
 */
const getMockAnalytics = () => {
  const days = 30;
  const callsOverTime = [];
  const baseDate = new Date();

  for (let i = days; i >= 0; i--) {
    const date = new Date(baseDate);
    date.setDate(date.getDate() - i);
    callsOverTime.push({
      date: date.toISOString().split('T')[0],
      count: Math.floor(Math.random() * 50) + 20
    });
  }

  const transferred = 323;
  const notTransferred = 720;
  const total = transferred + notTransferred;
  const transferRate = (transferred / total) * 100;

  return {
    summary: {
      total_calls: total,
      average_duration_ms: 64000,
      average_latency_ms: 1849
    },
    calls_over_time: callsOverTime,
    transfer_rate: {
      transferred: transferred,
      not_transferred: notTransferred,
      transfer_rate: transferRate
    }
  };
};

const analyticsApi = {
  getAnalyticsSummary
};

export default analyticsApi;
